/**
 * Settings: what the map draws, and nothing about the world.
 *
 * The line this screen holds is that a setting is a **way of looking**, not a
 * way of playing. Aerial imagery belongs here because it changes what is under
 * the map and changes nothing about the county. Routing around cameras does
 * not: that costs you distance, the server charges it, and it lives on the
 * action bar with the other things you do.
 *
 * Imagery was on the action bar at first, which put a display preference in the
 * row of controls somebody is aiming at while driving -- next to "stop" and
 * "pull over", both of which do something to the world.
 *
 * A row is a title, one line saying what it does, and a switch. It carried a
 * paragraph under the imagery toggle explaining that tiles are fetched from a
 * server; that is a lecture in a settings menu, and where the tiles come from
 * belongs in the citations panel with every other source.
 */

export interface Toggle {
  name: string;
  title: string;
  detail: string;
  on: () => boolean;
  available?: () => boolean;
  unavailable?: string;
  flip: () => void;
}

export class SettingsPanel {
  private readonly list: HTMLElement;
  private readonly toggles: readonly Toggle[];

  constructor(toggles: readonly Toggle[]) {
    this.toggles = toggles;
    this.list = must('settings-list');
    this.list.addEventListener('click', (event) => {
      const name = (event.target as HTMLElement).closest<HTMLElement>('.setting-row')?.dataset
        .setting;
      if (!name) return;
      const toggle = this.toggles.find((one) => one.name === name);
      if (!toggle) return;
      if (toggle.available && !toggle.available()) return;
      toggle.flip();
      this.paint();
    });
    this.paint();
  }

  paint(): void {
    this.list.replaceChildren();

    for (const toggle of this.toggles) {
      const available = toggle.available?.() ?? true;
      const on = toggle.on();

      const row = document.createElement('div');
      row.className = available ? 'setting-row' : 'setting-row off';
      row.dataset.setting = toggle.name;

      const text = document.createElement('div');
      text.className = 'setting-text';

      const title = document.createElement('div');
      title.className = 'setting-title';
      title.textContent = toggle.title;

      const detail = document.createElement('div');
      detail.className = 'setting-detail';
      detail.textContent = available ? toggle.detail : (toggle.unavailable ?? toggle.detail);

      text.append(title, detail);

      const knob = document.createElement('span');
      knob.className = on ? 'setting-switch on' : 'setting-switch';
      knob.setAttribute('role', 'switch');
      knob.setAttribute('aria-checked', String(on));

      row.append(text, knob);
      this.list.append(row);
    }
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
